import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ReviewBadges } from '../components/ReviewBadges';
import { StreamGapsBoard } from '../components/StreamGapsBoard';
import { TruncatedText } from '../components/TruncatedText';
import { useAppConfig } from '../state/config';
import { useCoreOnly } from '../state/coreOnly';
import { useGaps } from '../state/gaps';
import { useGeoFilter } from '../state/geoFilter';
import { useReviews } from '../state/reviews';
import { useStreamForms } from '../state/streams';
import { streamLabel } from '../stream-meta';
import type { Review, StreamFormRow } from '../types';
import { GEO_ALL } from '../types';

/**
 * Stream-detail page (spec §3 step 3 + §9.1).
 *
 * Reads /api/streams/:stream/forms with the active filters from the top bar
 * (geography, unreviewed-only) and the persistent core-only toggle. Core
 * forms render grouped by category; non-core forms (toggle off) render in a
 * single "Other forms" section at the bottom.
 */
export function StreamScreen() {
  const { streamId } = useParams<{ streamId: string }>();
  const streamName = streamId ?? '';
  const { geographies } = useAppConfig();
  const { coreOnly } = useCoreOnly();
  const { geo, unreviewedOnly } = useGeoFilter(undefined);

  const [filterText, setFilterText] = useState('');

  const filters = useMemo(
    () => ({
      core_only: coreOnly,
      geography: geo === GEO_ALL ? undefined : geo,
      // Server filter is conservative; for the "all geos" view we still apply
      // an extra client filter against `geographies.length` because the server
      // can't know which geographies the *user* considers in scope.
      unreviewed_only: unreviewedOnly && geo !== GEO_ALL,
    }),
    [coreOnly, geo, unreviewedOnly],
  );

  const { forms, loading, error, refresh } = useStreamForms(streamName, filters);

  const reviewFilters = useMemo(
    () => ({
      stream: streamName,
      geography: geo === GEO_ALL ? undefined : geo,
    }),
    [streamName, geo],
  );
  const { byKb: reviewsByKb, refresh: refreshReviews } = useReviews(reviewFilters);
  const { gaps } = useGaps(reviewFilters);

  const gapCountByForm = useMemo(() => {
    const m = new Map<string, number>();
    for (const g of gaps) {
      const key = g.kb_number ?? (g.form_sys_id ? `sys/${g.form_sys_id}` : null);
      if (!key) continue;
      m.set(key, (m.get(key) ?? 0) + 1);
    }
    return m;
  }, [gaps]);

  const lowerFilter = filterText.trim().toLowerCase();
  const filteredForms = useMemo(() => {
    return forms.filter((f) => {
      if (lowerFilter) {
        const hay =
          `${f.code ?? ''} ${f.name ?? ''} ${f.short_description ?? ''}`
            .toLowerCase();
        if (!hay.includes(lowerFilter)) return false;
      }
      if (unreviewedOnly && geo === GEO_ALL) {
        // Mirror the server's "all geos" semantics: drop forms reviewed in
        // every configured geography.
        const reviewKey = formReviewKey(f);
        const completed = reviewsByKb.get(reviewKey)?.size ?? 0;
        if (completed >= geographies.length) return false;
      }
      return true;
    });
  }, [forms, lowerFilter, unreviewedOnly, geo, reviewsByKb, geographies.length]);

  const grouped = useMemo(() => groupByCategory(filteredForms), [filteredForms]);

  if (error) {
    return (
      <PageShell>
        <Breadcrumb />
        <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-md px-4 py-3 mt-4">
          Couldn&apos;t load forms for {streamName}: {error}
        </div>
      </PageShell>
    );
  }
  if (loading && forms.length === 0) {
    return (
      <PageShell>
        <Breadcrumb />
        <div className="text-slate-500 mt-4">Loading {streamName}…</div>
      </PageShell>
    );
  }

  const totalShown = filteredForms.length;
  const headerLabel = streamLabel(streamName) || streamName;

  return (
    <PageShell>
      <Breadcrumb />

      <header className="flex flex-wrap items-end justify-between gap-3 mt-2">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            {streamName}
            <span className="ml-2 text-sm font-normal text-slate-500">
              {headerLabel !== streamName ? headerLabel : ''}
            </span>
          </h1>
          <p className="text-slate-600 text-sm">
            {totalShown} form{totalShown === 1 ? '' : 's'} shown
            {!coreOnly && (
              <span className="text-slate-500"> · core + non-core</span>
            )}
            {unreviewedOnly && (
              <span className="text-slate-500"> · pending review only</span>
            )}
          </p>
        </div>
        <input
          className="input h-9 max-w-xs"
          placeholder="Filter by code, name, or description…"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
      </header>

      <div className="space-y-6 mt-6">
        {grouped.coreCategories.map(({ category, forms: rows }) => (
          <CategorySection
            key={category}
            streamId={streamName}
            heading={category}
            forms={rows}
            reviewsByKb={reviewsByKb}
            gapCountByForm={gapCountByForm}
            topBarGeo={geo}
            onReviewChanged={() => {
              refreshReviews();
              refresh();
            }}
          />
        ))}
        {grouped.nonCoreForms.length > 0 && (
          <CategorySection
            streamId={streamName}
            heading="Other forms"
            subtext="Record producers not documented in the design-repository KB"
            forms={grouped.nonCoreForms}
            reviewsByKb={reviewsByKb}
            gapCountByForm={gapCountByForm}
            topBarGeo={geo}
            onReviewChanged={() => {
              refreshReviews();
              refresh();
            }}
          />
        )}
        {totalShown === 0 && (
          <div className="text-sm text-slate-500 italic px-4 py-6">
            No forms match the current filters.
          </div>
        )}
      </div>

      <div className="mt-10">
        <StreamGapsBoard stream={streamName} topBarGeo={geo} />
      </div>
    </PageShell>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────

function formReviewKey(f: StreamFormRow): string {
  return f.kb_number ?? `sys/${f.producer_sys_id}`;
}

function groupByCategory(forms: StreamFormRow[]) {
  const coreByCat = new Map<string, StreamFormRow[]>();
  const nonCore: StreamFormRow[] = [];
  for (const f of forms) {
    if (!f.is_core) {
      nonCore.push(f);
      continue;
    }
    const cat = f.category ?? 'Uncategorised';
    if (!coreByCat.has(cat)) coreByCat.set(cat, []);
    coreByCat.get(cat)!.push(f);
  }
  const coreCategories = Array.from(coreByCat.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([category, rows]) => ({ category, forms: rows }));
  return { coreCategories, nonCoreForms: nonCore };
}

// ─── Subcomponents ─────────────────────────────────────────────────────

function CategorySection({
  streamId,
  heading,
  subtext,
  forms,
  reviewsByKb,
  gapCountByForm,
  topBarGeo,
  onReviewChanged,
}: {
  streamId: string;
  heading: string;
  subtext?: string;
  forms: StreamFormRow[];
  reviewsByKb: Map<string, Map<string, Review>>;
  gapCountByForm: Map<string, number>;
  topBarGeo: string;
  onReviewChanged: () => void;
}) {
  if (forms.length === 0) return null;
  return (
    <section>
      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">
        {heading}
        <span className="ml-2 text-slate-400 normal-case tracking-normal">
          ({forms.length})
        </span>
      </h3>
      {subtext && (
        <p className="text-xs text-slate-400 mb-2 -mt-1">{subtext}</p>
      )}
      <div className="card divide-y divide-slate-100 overflow-hidden">
        {forms.map((f) => (
          <FormRow
            key={f.producer_sys_id}
            streamId={streamId}
            form={f}
            reviewsByGeo={reviewsByKb.get(formReviewKey(f))}
            gapCount={gapCountByForm.get(formReviewKey(f)) ?? 0}
            topBarGeo={topBarGeo}
            onReviewChanged={onReviewChanged}
          />
        ))}
      </div>
    </section>
  );
}

function FormRow({
  streamId,
  form,
  reviewsByGeo,
  gapCount,
  topBarGeo,
  onReviewChanged,
}: {
  streamId: string;
  form: StreamFormRow;
  reviewsByGeo: Map<string, Review> | undefined;
  gapCount: number;
  topBarGeo: string;
  onReviewChanged: () => void;
}) {
  const href = form.is_core && form.kb_number
    ? `/streams/${encodeURIComponent(streamId)}/form/${encodeURIComponent(form.kb_number)}`
    : `/streams/${encodeURIComponent(streamId)}/form/sys/${form.producer_sys_id}`;
  return (
    <Link
      to={href}
      className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <TruncatedText
            text={form.name ?? ''}
            className="text-sm font-medium text-slate-900 min-w-0 flex-shrink"
          />
          <ScopeBadge isCore={form.is_core} />
        </div>
        {form.short_description && (
          <TruncatedText
            text={form.short_description}
            className="text-xs text-slate-500"
          />
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <ReviewBadges
          kbNumber={formReviewKey(form)}
          reviewsByGeo={reviewsByGeo}
          topBarGeo={topBarGeo}
          stream={streamId}
          formSysId={form.producer_sys_id}
          onChanged={onReviewChanged}
        />
        {gapCount > 0 && (
          <span
            className="chip bg-amber-50 text-amber-800 border border-amber-200"
            title={`${gapCount} gap${gapCount === 1 ? '' : 's'} logged`}
          >
            {gapCount} gap{gapCount === 1 ? '' : 's'}
          </span>
        )}
      </div>
    </Link>
  );
}

function ScopeBadge({ isCore }: { isCore: boolean }) {
  if (isCore) {
    return (
      <span
        className="shrink-0 inline-flex items-center text-[10px] font-semibold uppercase tracking-wide rounded-full px-1.5 py-0.5 bg-brand-100 text-brand-danone"
        title="Core form (KB-documented)"
      >
        Core
      </span>
    );
  }
  return (
    <span
      className="shrink-0 inline-flex items-center text-[10px] font-semibold uppercase tracking-wide rounded-full px-1.5 py-0.5 bg-amber-100 text-amber-800"
      title="Local / non-core form (no KB article)"
    >
      Local
    </span>
  );
}

function Breadcrumb() {
  return (
    <nav className="text-sm">
      <Link to="/" className="text-brand-danone hover:underline">
        ← All streams
      </Link>
    </nav>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  useAppConfig();
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</div>
  );
}
